import { createStore } from "vuex";
import axios from "axios";
import $ from "jquery";
import { apiEndpoints, setAccessToken } from "@/config/api";
import { g } from "@/config/global";
import { GameType } from "@/types/base";
import i18n from "@/i18n";
import { executeApiCall } from "@/lib/error-handler";

const store = createStore({
  state: {
    loading: false,
    casinoList: [],
    slotGameList: [],
    allSlotGameList: [],
    miniGameList: [],
    newCasinoList: [],
    newSlotGameList: [],
    hotCasinoList: [],
    hotSlotGameList: [],
    bankingHistory: [],
    bankingHistoryTotal: 0,
    bettingHistory: [],
    bettingHistoryTotal: 0,
    inquiries: [],
    inquiriesTotal: 0,
    notices: [],
    noticesTotal: 0,
    notificationPopup: false,
    maintenancePopup: false,
    homePopup: false,
    homePopupTab: "tab1",
    itemGamePopup: false,
    loggedIn: false,
    settings: {},
    user: {},
  },
  getters: {},
  mutations: {
    setLoading(state, payload) {
      state.loading = payload;
    },
    setCasinoList(state, payload) {
      state.casinoList = payload;
    },
    setSlotGameList(state, payload) {
      state.slotGameList = payload;
    },
    setAllSlotGameList(state, payload) {
      state.allSlotGameList = payload;
    },
    setMiniGameList(state, payload) {
      state.miniGameList = payload;
    },
    setCasinoListNew(state, payload) {
      state.newCasinoList = payload;
    },
    setCasinoListHot(state, payload) {
      state.hotCasinoList = payload;
    },
    setSlotGameListNew(state, payload) {
      state.newSlotGameList = payload;
    },
    setSlotGameListHot(state, payload) {
      state.hotSlotGameList = payload;
    },
    setBankingHistory(state, { list, total }) {
      state.bankingHistory = list;
      state.bankingHistoryTotal = total;
    },
    setBettingHistory(state, { list, total }) {
      state.bettingHistory = list;
      state.bettingHistoryTotal = total;
    },
    setInquiries(state, { list, total }) {
      state.inquiries = list;
      state.inquiriesTotal = total;
    },
    setNotices(state, { list, total }) {
      state.notices = list;
      state.noticesTotal = total;
    },
    showNotificationPopup(state, payload) {
      state.notificationPopup = payload;
    },
    showMaintenancePopup(state, payload) {
      state.maintenancePopup = payload;
    },
    showHomePopup(state, { open, tab }) {
      state.homePopup = open;
      if (state.loggedIn) {
        state.homePopupTab = tab;
        $("body").css("overflow", open ? "hidden" : "auto");
      } else {
        state.homePopup = false;
        g.globalProperties.$swal.error({
          title: i18n.global.t("common.loginRequired"),
        });
      }
    },
    showSlotGamePopup(state, { open }) {
      state.itemGamePopup = open;
    },
    setSettings(state, payload) {
      const maintenance = payload.settings.find(
        (setting) => setting.key === "maintenance"
      )?.value;
      const enabled_game_types = payload.settings.find(
        (setting) => setting.key === "enabled_game_types"
      )?.value;
      const dsp_currency = payload.settings.find(
        (setting) => setting.key === "dsp_currency",
      )?.value;
      const usdt_exchange_rate = payload.settings.find(
        (setting) => setting.key === "usdt_exchange_rate",
      )?.value;
      const lobby_visibility = payload.settings.find(
        (setting) => setting.key === "lobby_visibility",
      )?.value;
      state.settings = {
        maintenance,
        enabled_game_types,
        dsp_currency,
        usdt_exchange_rate: JSON.parse(usdt_exchange_rate),
        lobby_visibility,
        isSportsOn: enabled_game_types?.includes(GameType.SPORTS),
      };
    },
    setUser(state, payload) {
      const { access_token, user } = payload;

      if (access_token) {
        setAccessToken(access_token);
      }

      if (user) {
        const chip = Number(user.chip);
        const point = Number(user.point);
        const newUser = {
          ...state.user,
          ...user,
          chip,
          point,
          totalAsset: chip + point,
          chipStr: new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
          }).format(chip),
          pointStr: new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
          }).format(point),
          totalAssetStr: new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
          }).format(chip + point),
        };

        state.user = newUser;
        g.globalProperties.$cookies.set("user", JSON.stringify(newUser), "1h");

        state.loggedIn = true;
      } else {
        state.user = {};
        state.loggedIn = false;
      }
    },
  },
  actions: {
    showNotificationPopup(context, open) {
      context.commit("showNotificationPopup", open);
    },
    showMaintenancePopup(context, open) {
      context.commit("showMaintenancePopup", open);
    },
    showHomePopup(context, { open, tab }) {
      const user = context.state.user;
      if (user) {
        context.commit("showHomePopup", { open, tab });
      } else {
        context.commit("showNotificationPopup", true);
      }
    },
    setSettings(context, payload) {
      context.commit("setSettings", payload);
    },
    setUser(context, payload) {
      context.commit("setUser", payload);
    },
    clearUser(context) {
      context.commit("setUser", {});
    },
    async fetchSettings(context) {
      const { data } = await axios.get(apiEndpoints.api_settings);
      if (data) {
        context.commit("setSettings", data);
      }
      return data;
    },
    async fetchRefreshToken(context) {
      try {
        const { data } = await axios.post(apiEndpoints.api_refreshToken);
        return data;
      } catch (error) {
        context.commit("setUser", {});
        throw error;
      }
    },
    async fetchMyInfo(context) {
      const data = await executeApiCall(() =>
        axios.get(apiEndpoints.api_myInfo)
      );
      if (data) {
        context.commit("setUser", data);
      } else {
        context.commit("setUser", {});
      }
      return data;
    },
    async patchMyInfo(context, { password, passwordNew, successMessage }) {
      await executeApiCall(
        () =>
          axios.patch(apiEndpoints.api_updateMyInfo, {
            password,
            password_new: passwordNew,
          }),
        successMessage || i18n.global.t("common.success")
      );
    },
    fetchLobbyList(context) {
      const lang = localStorage.getItem('userLocale');
      const url = `${apiEndpoints.api_lobbyList}?lang=${lang}`;
      axios.get(url).then((response) => {
        const data = response.data;
        // 이미지 경로 설정을 위한 공통 함수
        const setProviderImage = (item, type) => {
          const code = item.etc1;
          if (type === GameType.CASINO) {
            item.providerImage = `/assets/img/providers/${type.toLowerCase()}/${code}.png`;
            item.providerImageText = ""; //`/assets/img/providers/${type}/${name}_text.png`;
          } else if (type === GameType.SLOT) {
            item.providerImage = `/assets/img/providers/${type.toLowerCase()}/${code}.png`;
            item.providerImageText = "";
          }
          return true;
        };

        // 카지노 게임 이미지 설정
        const casino = data.casino.filter((item) => {
          return setProviderImage(item, GameType.CASINO);
        });

        // 슬롯 게임 이미지 설정
        const slot = data.slot.filter((item) => {
          return setProviderImage(item, GameType.SLOT);
        });

        context.commit("setCasinoList", casino);
        context.commit("setSlotGameList", slot);
      });
    },
    // eslint-disable-next-line
    fetchNewGameList(context) {
      // TODO: 추후 사용 예정
      // axios.get(apiEndpoints.api_gamecasinoNew).then((response) => {
      //   const casino = [];
      //   const slot = [];
      //   for (const d of response.data.data) {
      //     if (d.bType === GameType.CASINO) {
      //       casino.push(d);
      //     }
      //     if (d.bType === GameType.SLOT) {
      //       slot.push(d);
      //     }
      //   }
      //   context.commit("setCasinoListNew", casino);
      //   context.commit("setSlotGameListNew", slot);
      // });
    },
    // eslint-disable-next-line
    fetchHotGameList(context) {
      // TODO: 추후 사용 예정
      // axios.get(apiEndpoints.api_gamecasinoHot).then((response) => {
      //   const casino = [];
      //   const slot = [];
      //   for (const d of response.data.data) {
      //     if (d.bType === GameType.CASINO) {
      //       casino.push(d);
      //     }
      //     if (d.bType === GameType.SLOT) {
      //       slot.push(d);
      //     }
      //   }
      //   context.commit("setCasinoListHot", casino);
      //   context.commit("setSlotGameListHot", slot);
      // });
    },
    async fetchBankingHistory(context, { page, perPage }) {
      const response = await executeApiCall(() =>
        axios.get(apiEndpoints.api_bankingHistory, {
          params: {
            page: Number(page) || undefined,
            limit: Number(perPage) || undefined,
          },
        })
      );
      if (response) {
        const { list, total } = response;
        context.commit("setBankingHistory", { list, total });
      }
    },
    async fetchBettingHistory(context, { page, perPage }) {
      const response = await executeApiCall(() =>
        axios.get(apiEndpoints.api_bettingHistory, {
          params: {
            page: Number(page) || undefined,
            limit: Number(perPage) || undefined,
          },
        })
      );
      if (response) {
        const { list, total } = response;
        context.commit("setBettingHistory", { list, total });
      }
    },
    async requestBanking(context, { deposit, amount, coinOrBankName }) {
      const user = context.state.user;
      await executeApiCall(
        () =>
          axios.post(apiEndpoints.api_deposit, {
            type: deposit ? "RECHARGE" : "EXCHANGE",
            amount: Number(amount),
            bank_name: coinOrBankName,
            bank_account: user.bank_account,
            bank_owner: user.bank_owner,
          }),
        i18n.global.t("common.success")
      );
    },
    async fetchInquiries(context, { page, perPage }) {
      const response = await executeApiCall(() =>
        axios.get(apiEndpoints.api_inquiries, {
          params: {
            page: Number(page) || undefined,
            limit: Number(perPage) || undefined,
          },
        })
      );
      if (response) {
        const { list, total } = response;
        context.commit("setInquiries", { list, total });
      }
    },
    async postInquiry(context, { title, content }) {
      const response = await executeApiCall(() =>
        axios.post(apiEndpoints.api_writeInquiry, {
          title,
          content,
        })
      );
      if (response) {
        return response;
      }
    },
    async deleteInquiry(context, { id }) {
      await executeApiCall(
        () => axios.delete(`${apiEndpoints.api_inquiries}/${id}`),
        i18n.global.t("common.success")
      );
    },
    async fetchNotices(context, { page, perPage }) {
      const response = await executeApiCall(() =>
        axios.get(apiEndpoints.api_notices, {
          params: {
            page: Number(page) || undefined,
            limit: Number(perPage) || undefined,
          },
        })
      );
      if (response) {
        const { list, total } = response;
        context.commit("setNotices", { list, total });
      }
    },
    async getGameProcess(context, { lang, gameInfo }) {
      if (context.state.loading) {
        return;
      }

      console.debug("getGameProcess", {
        lang,
        gameInfo,
      });

      context.commit("setLoading", true);
      $("#loading").show().fadeIn(10);

      const response = await executeApiCall(() =>
        axios.post(apiEndpoints.api_gamePlay, {
          lang,
          gameInfo,
        })
      );

      if (response?.gameUrl) {
        const size = {
          width: window.innerWidth || document.body.clientWidth,
          height: window.innerHeight || document.body.clientHeight,
        };
        //창 크기 지정
        const width = size.width;
        const height = size.height;
        //pc화면기준 가운데 정렬
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 4;
        //윈도우 속성 지정
        const windowStatus =
          "width=" +
          width +
          ", height=" +
          height +
          ", left=" +
          left +
          ", top=" +
          top +
          ", scrollbars=yes, status=yes, resizable=yes, titlebar=yes";
        //등록된 url 및 window 속성 기준으로 팝업창을 연다.
        window.open(response.gameUrl, "gamepopup", windowStatus);
      }

      context.commit("setLoading", false);
      $("#loading").css("display", "none");
    },
    async showSlotGamePopup(context, { open, lang, gameInfo }) {
      console.debug("showSlotGamePopup", {
        open,
        lang,
        gameInfo,
      });

      if (!open) {
        context.commit("showSlotGamePopup", { open });
        return;
      }

      const user = context.state.user;
      if (!user) {
        return context.commit("showNotificationPopup", true);
      }

      $("#loading").show().fadeIn(10);

      const params = new URLSearchParams();
      const gi = {
        ...gameInfo,
      };
      delete gi.providerImage;
      delete gi.gameImage;
      params.append("lang", lang);
      params.append("gameInfo", JSON.stringify(gi));

      const response = await executeApiCall(() =>
        axios.get(`${apiEndpoints.api_gameList}?${params.toString()}`)
      );

      if (response?.list) {
        context.commit("setAllSlotGameList", response.list);
        context.commit("showSlotGamePopup", { open });

        setTimeout(() => {
          $(".namegaming").text(gameInfo.providerName);
        }, 100);
      }

      $("#loading").css("display", "none");
    },
  },
});

export default store;
